import { Controller } from "@hotwired/stimulus";
import * as Turbo from "@hotwired/turbo";
import { htmlEncode } from "../helpers";

interface ApiTokensModel { email: string | null; getJobUrl: string; statusUrl: string; }
interface RevocationJob { completedOn: string | null; exception: string | null; numberOfHandlingAttempts: number; }
interface RevocationStatus { tokensValidAfterUtc: string | null; }

export default class ApiTokensController extends Controller {
    static targets = ["form", "submitButton", "status", "success", "cutoff"];
    readonly formTarget!: HTMLFormElement;
    readonly submitButtonTarget!: HTMLButtonElement;
    readonly statusTarget!: HTMLElement;
    readonly successTarget!: HTMLElement;
    readonly cutoffTarget!: HTMLElement;
    private _busy = false;

    revoke(event: Event) {
        event.preventDefault();
        if (this._busy) return;
        this._busy = true;
        const model = JSON.parse(this.data.get("model")!) as ApiTokensModel;
        bootbox.confirm({
            title: "Revoke existing API tokens",
            message: `Revoke all existing API tokens for <strong>${htmlEncode(model.email || "this user")}</strong>? ` +
                "Integrations using this account will need a new token. Tokens issued up to this revocation will stop working. " +
                "This cannot be undone. CMS browser sessions stay signed in. Other users are unaffected.",
            buttons: { confirm: { label: "Revoke API tokens", className: "btn-danger" }, cancel: { label: "Cancel" } },
            callback: (confirmed) => {
                if (!confirmed) { this._busy = false; return; }
                if (!this.element.isConnected) { this._busy = false; return; }
                void this._Submit(model);
            }
        });
    }

    private async _Submit(model: ApiTokensModel) {
        const button = this.submitButtonTarget;
        const label = button.textContent;
        button.disabled = true;
        button.textContent = "Revoking…";
        this.statusTarget.hidden = true;
        this.successTarget.hidden = true;
        try {
            const response = await fetch(this.formTarget.action, {
                method: "POST", body: new FormData(this.formTarget), credentials: "same-origin", headers: { Accept: "application/json" }
            });
            if (!response.ok) throw new Error("Revocation request failed.");
            const text = await response.text();
            const commandGuid = response.headers.get("content-type")?.includes("application/json") ? JSON.parse(text) as string : text.trim();
            if (!/^[0-9a-f-]{36}$/i.test(commandGuid)) throw new Error("Missing command identity.");
            let completed = false;
            for (let attempt = 0; attempt < 900; attempt++) {
                await this._Delay(attempt === 0 ? 500 : 1000);
                if (!this.element.isConnected) return;
                const job = await this._Read<RevocationJob>(model.getJobUrl.replace("$guid", encodeURIComponent(commandGuid)));
                if (!this.element.isConnected) return;
                if (job.completedOn) { completed = true; break; }
                if (job.exception && job.numberOfHandlingAttempts >= 5) throw new Error("Revocation job failed.");
            }
            if (!completed) throw new Error("Revocation completion timed out.");
            const status = await this._Read<RevocationStatus>(model.statusUrl);
            if (!this.element.isConnected) return;
            if (!status.tokensValidAfterUtc) throw new Error("Revocation could not be confirmed.");
            this.cutoffTarget.textContent = new Date(status.tokensValidAfterUtc).toISOString().slice(0, 19).replace("T", " ") + " UTC";
            this.successTarget.hidden = false;
            Turbo.clearCache();
        } catch {
            if (!this.element.isConnected) return;
            this.statusTarget.textContent = "Could not confirm revocation. Refresh the user details before retrying.";
            this.statusTarget.hidden = false;
        } finally {
            this._busy = false;
            button.disabled = false;
            button.textContent = label;
        }
    }

    private async _Read<T>(url: string): Promise<T> {
        const response = await fetch(url, { credentials: "same-origin", cache: "no-store", headers: { Accept: "application/json" } });
        if (!response.ok) throw new Error("Revocation status is unavailable.");
        return await response.json() as T;
    }

    private _Delay(milliseconds: number) { return new Promise<void>(resolve => setTimeout(resolve, milliseconds)); }
}
